/**
 * ETA do AIS → instante utilizável — funções puras (testadas em `eta.test.ts`).
 *
 * A ETA da mensagem estática (tipo 5) é um campo de 20 bits com mês, dia, hora
 * e minuto — **e nenhum ano**. Pior: quase sempre vem inválida, porque o campo
 * é digitado à mão na ponte e muitos transponders nunca são atualizados.
 *
 * Medido na Amazônia em 20/09/2026: de 44 embarcações com dado estático, 35
 * traziam ETA, e entre elas apareceram `0/0 0:00` (não preenchida) e
 * `24:60` (o "não disponível" da norma, gravado como hora 24 e minuto 60).
 *
 * Mostrar `24:60` ou `1/0` para o cidadão seria vazar o dado cru na cara dele.
 * Aqui, ETA inválida vira `null`, e a interface escreve "não informada".
 */

/** ETA como vem na mensagem estática do AIS. */
export interface EtaCrua {
  Month?: number | null;
  Day?: number | null;
  Hour?: number | null;
  Minute?: number | null;
}

/**
 * Valida e resolve o ano. Devolve epoch ms UTC, ou `null` se a ETA não é uma
 * data de verdade.
 *
 * O ano é o que põe a ETA MAIS PERTO de `agora` — um navio que anuncia
 * "3 de janeiro" em 28 de dezembro está falando do ano que vem, e um que
 * anuncia "28 de dezembro" em 3 de janeiro está falando do ano passado
 * (atrasado, mas é o que ele disse). Testar os três anos candidatos e ficar
 * com o mais próximo resolve os dois casos sem regra especial de virada.
 */
export function resolverEta(eta: EtaCrua | null | undefined, agora: number = Date.now()): number | null {
  if (!eta) return null;
  const mes = num(eta.Month);
  const dia = num(eta.Day);
  const hora = num(eta.Hour);
  const min = num(eta.Minute);

  // 0 = "não disponível" na norma, para mês e dia.
  if (mes === null || dia === null || mes < 1 || mes > 12 || dia < 1 || dia > 31) return null;
  // Hora 24 e minuto 60 são o "não disponível" de hora/minuto.
  if (hora === null || min === null || hora > 23 || min > 59 || hora < 0 || min < 0) return null;

  const anoAgora = new Date(agora).getUTCFullYear();
  let melhor: number | null = null;
  for (const ano of [anoAgora - 1, anoAgora, anoAgora + 1]) {
    const ms: number = Date.UTC(ano, mes - 1, dia, hora, min);
    // Date.UTC "conserta" 31 de fevereiro virando 2 ou 3 de março. Uma data
    // que não existe não é uma ETA: conferimos que voltou o mesmo dia.
    const d: Date = new Date(ms);
    if (d.getUTCMonth() !== mes - 1 || d.getUTCDate() !== dia) continue;
    if (melhor === null || Math.abs(ms - agora) < Math.abs(melhor - agora)) melhor = ms;
  }
  return melhor;
}

function num(x: number | null | undefined): number | null {
  return typeof x === 'number' && Number.isFinite(x) ? Math.trunc(x) : null;
}

/**
 * Quão longe a ETA está de `agora`, para a interface decidir se ela ainda faz
 * sentido. Uma ETA de três meses atrás é um transponder esquecido, não uma
 * previsão — e anunciá-la como "chegada prevista" seria mentira.
 */
export const ETA_VELHA_MS = 7 * 24 * 60 * 60 * 1000;

export function etaObsoleta(ms: number | null, agora: number = Date.now()): boolean {
  return ms !== null && agora - ms > ETA_VELHA_MS;
}
